import z from 'zod'
import {
  firestoreTimestamp,
  firestoreWriteTimestamp,
  nullSafeFirestoreTimestamp,
  nullSafeString,
} from '../shared/schema'

export enum FirestoreRoomMessageStatus {
  Delivered = 'delivered',
  Error = 'error',
  Seen = 'seen',
  Sending = 'sending',
  Sent = 'sent',
}

export enum FirestoreRoomMessageType {
  Audio = 'audio',
  Custom = 'custom',
  File = 'file',
  Image = 'image',
  System = 'system',
  Unsupported = 'unsupported',
  Video = 'video',

  /// SUPPORTED
  /** only used by meeting */
  Text = 'text',
  /** special message type in meeting used for nudge*/
  Nudge = 'nudge',
  /** scheduling dialog messages by users */
  Scheduling = 'scheduling',
  /** system message generated by server -- only to be shown in scheduling dialog */
  SchedulingBot = 'schedulingBot',
}

const _systemMessagesBasicSchema = z.object({
  type: z.literal('soloBotPromptRendered'),
  userId: z.string(),
  promptType: z.string(),
})

const _systemMessagesUserJoinSchema = z.object({
  type: z.literal('join'),
  userId: z.string(),
})

const _systemMessageUserLeftSchema = z.object({
  type: z.literal('leave'),
  userId: z.string(),
})

const _systemMessageUserRescheduledSchema = z.object({
  type: z.literal('reschedule'),
  roomId: z.string(),
  newScheduledAt: firestoreTimestamp,
  previousScheduledAt: firestoreTimestamp,
  userId: z.string().optional(),
})

// need to store userIds in metadata
const _nudgeSchema = z.object({
  type: z.literal('nudge'),
  userIds: z.array(z.string()),
})

const _systemMessageSchema = z.union([
  _systemMessagesBasicSchema,
  _systemMessagesUserJoinSchema,
  _systemMessageUserLeftSchema,
  _systemMessageUserRescheduledSchema,
])

const metadataSchema = z.union([_systemMessageSchema, _nudgeSchema])

export const schema = z.object({
  authorId: z.string(),
  type: z.preprocess(
    (val) => val ?? FirestoreRoomMessageType.Text,
    z.nativeEnum(FirestoreRoomMessageType)
  ),
  status: z.preprocess(
    (val) => val ?? FirestoreRoomMessageStatus.Sending,
    z.nativeEnum(FirestoreRoomMessageStatus)
  ),
  seenBy: z.record(z.string(), firestoreTimestamp).optional(),
  // guarantee text to be a string
  text: nullSafeString(''),
  createdAt: nullSafeFirestoreTimestamp(),
  updatedAt: nullSafeFirestoreTimestamp(),
  nudgeSent: z.boolean().optional(),
  metadata: metadataSchema.optional(),
})

export const empty = Object.freeze(
  schema.parse({
    authorId: '',
    type: FirestoreRoomMessageType.Text,
    status: FirestoreRoomMessageStatus.Sending,
    text: '',
    createdAt: { seconds: 0, nanoseconds: 0 },
    updatedAt: { seconds: 0, nanoseconds: 0 },
  })
)

export const writeSchema = schema
  .extend({
    seenBy: z.record(z.string(), firestoreWriteTimestamp).optional(),
    createdAt: firestoreWriteTimestamp,
    updatedAt: firestoreWriteTimestamp,
  })
  .omit({ status: true })

export type FirestoreRoomMessage = z.infer<typeof schema>

export type FirestoreRoomMessageWrite = Omit<FirestoreRoomMessage, 'status'>

export type SystemMessage = z.infer<typeof _systemMessageSchema>
